import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Typed wrappers around the admin-api customer routes (Phase 5d-3b, ADR-039).
 * The Lambda is the authority for response shapes; this module mirrors them.
 *
 * Role gates (ADR-012):
 *   - List / detail reads: support + nexa_admin + super_admin (MUTATING_TIER_READ)
 *   - Create / override mutations: nexa_admin + super_admin (MUTATING)
 *   - All gated server-side; the portal hides action affordances by role too.
 */

export type SubscriptionState = 'active' | 'past_due' | 'suspended' | 'cancelled';

/** MVP plan/tier names — T1=NexaGrowth, T2=NexaSuccess (T3 deferred post-MVP). */
export const MVP_TIERS = ['NexaGrowth', 'NexaSuccess'] as const;
export type MvpTier = typeof MVP_TIERS[number];

export const SUBSCRIPTION_STATES: SubscriptionState[] = [
  'active',
  'past_due',
  'suspended',
  'cancelled',
];

export const SUBSCRIPTION_STATE_LABELS: Record<SubscriptionState, string> = {
  active: 'Active',
  past_due: 'Past due',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

export interface CustomerListItem {
  accountId: string;
  customerNumber: string;
  businessName: string;
  email: string;
  /** The staff-facing plan LABEL on the account row (may be empty). "Remove plan" acts on this. */
  plan: string;
  /** The plan billing bills the account for, sent only when `plan` is empty (Jeff, 6 October
   *  2026). Display only — see `shownPlan`. */
  billingPlan?: string;
  subscriptionState: SubscriptionState;
  subscriptionStateChangedAt: string;
  createdAt: string;
  /** Website captured at manual-create (5d-3b D4). */
  website: string;
  /** First active domain hostname, else first domain, else website (5d-3b D9). */
  primaryDomain: string;
  /** D5 — how many domains the account holds, and their state. Absent when the server's
   *  per-account lookup failed — rendered as unknown, never as zero. */
  domainCounts?: { total: number; active: number; pending: number };
  /** Beta-onboarded account (ADR-073 Am.2 A2.5). Flippable state, not a tag. */
  beta: boolean;
  /** Onboarding Nexa consultant (ADR-073 Am.2 A2.4). Blank for non-beta. */
  consultant: string;
  /**
   * PRESENT ⇒ this row is an operator/management ORG (`beta` / `consultant` /
   * `partner`), not a customer — ADR-112 D4's authoritative discriminator.
   *
   * Optional because it is absent on every customer, which is every row these
   * screens are for. It exists so the destructive + billing row actions can be
   * withheld on a row REACHED BY ID (direct URL, bookmark, staff node picker)
   * rather than through the filtered Customers list, which PR #440 already
   * excludes operator orgs from. The server refuses each action regardless
   * (`operator_org_not_deletable` / `operator_org_not_creditable` /
   * `operator_org_not_editable` for a plan write); this keeps the UI from offering a
   * control the server would reject (ADR-012).
   */
  operatorPortalType?: string;

  /**
   * ADR-120 P8 step 7(b) — which zone this record actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API
   * answers only for itself and has no reason to name itself. Always populated for a row that
   * came through `listAllCustomers`.
   */
  zone?: string;
}

/** The plan to SHOW for a customer: the account's label, else what billing bills it for, else ''. */
export function shownPlan(c: Pick<CustomerListItem, 'plan' | 'billingPlan'>): string {
  return c.plan || c.billingPlan || '';
}

export interface CustomerDetail extends CustomerListItem {
  createdBy: string;
  /** Plan expiry date — display-only at 5d-3b; populated by billing in 5d-4 (D15). */
  planExpiry?: string;
  subscriptionStateReason?: string;
  subscriptionHistory: SubscriptionHistoryEntry[];
  domains: DomainRow[];
  /** PL39 (ADR-090) — account setupState; terminal 'active' == onboarded. */
  setupState?: string;
  /** PL39 (ADR-090) — derived server-side: setupState === 'active'. Launch
   *  onboarding is disabled on the console when true. */
  onboarded?: boolean;
}

export interface SubscriptionHistoryEntry {
  ts: string;
  fromState: SubscriptionState;
  toState: SubscriptionState;
  reason?: string;
  staffSub: string;
  staffId: string;
}

export interface DomainRow {
  domainToken: string;
  hostname: string;
  status: string;
  createdAt: string;
  /** D5 — has this domain ever sent data? `unknown` when the liveness read failed or was slow. */
  everSentData?: 'yes' | 'no' | 'unknown';
}

/** D5 — the Customers list's domain summary, e.g. `5 domains · 0 active · 5 pending`. */
export function domainCountsLabel(c: CustomerListItem['domainCounts']): string {
  if (!c) return 'domain count unavailable';
  return `${c.total} ${c.total === 1 ? 'domain' : 'domains'} · ${c.active} active · ${c.pending} pending`;
}

export interface CustomerAdminAuditEntry {
  action: string;
  performedBy: string;
  performedByStaffId: string;
  at: string;
  reviewNotes: string;
}

interface ListPage {
  items: CustomerListItem[];
  cursor: string | null;
}

/** Load all customers by following cursors until exhausted. */
/**
 * The result of a cross-zone customer read.
 *
 * ⭐ `failedZones` IS NOT OPTIONAL TELEMETRY — IT IS PART OF THE ANSWER. A zone that did not
 * respond contributes no rows, and a shorter list is indistinguishable from a correct one. An
 * operator searching for a customer would be told, in effect, that they do not exist. The
 * caller must render this; it is why the function no longer returns a bare array.
 */
/**
 * Route one call to a record's own zone (ADR-120 P8 step 7(b)).
 *
 * ⚠ EVERY PER-RECORD CALL TAKES A ZONE, AND OMITTING IT MEANS "HOME", NOT "ANY". A customer
 * exists in exactly one zone; asking ap-southeast-2 about a us-east-1 account returns 404, and
 * a 404 on a record the operator is LOOKING AT reads as data loss rather than as a misrouted
 * request. The list tags every row with `zone` precisely so the detail view can pass it back.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

export interface CustomerListResult {
  items: CustomerListItem[];
  /** Labels of zones that did not answer. Empty on a complete read. */
  failedZones: string[];
}

/**
 * Page through one zone's customers, tagging every row with the zone it came from.
 *
 * Exported (27 September 2026) for the one page that reads a SINGLE zone by policy rather
 * than by omission: Assign Customers lists only the consultant org's own zone, because
 * consultants serve their own data region only (ADR-116 Status note).
 */
export async function listCustomersInZone(zone: PortalZone): Promise<CustomerListItem[]> {
  const all: CustomerListItem[] = [];
  let cursor: string | null = null;
  do {
    const qs: string = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const page: ListPage = await adminApiInZone<ListPage>(zone, `/admin/customers${qs}`);
    all.push(...page.items.map((it) => ({ ...it, zone: zone.code })));
    cursor = page.cursor;
  } while (cursor);
  return all;
}

/**
 * Every customer the operator may see, across every zone (ADR-120 P8 step 7(b)).
 *
 * The fan-out runs in THIS BROWSER, one request per zone, each carrying the same globally-valid
 * staff token — see `lib/zones.ts` for why that is a compliance requirement and not a style
 * choice. With one zone configured this is exactly the single-zone read it replaced.
 */
export async function listAllCustomers(): Promise<CustomerListResult> {
  const results = await fanOutZones(listCustomersInZone);
  return {
    // Newest first, so a merge of two zones reads as one list rather than as AU-then-US.
    items: results
      .flatMap((r) => r.items)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    failedZones: failedZoneLabels(results),
  };
}

export async function getCustomer(accountId: string,
  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<CustomerDetail> {
  return call<CustomerDetail>(zone, `/admin/customers/${encodeURIComponent(accountId)}`);
}

export async function createCustomer(body: {
  businessName: string;
  /** Required primary domain/website (5d-3b D4). */
  website: string;
  /** Optional for org accounts (5d-3b D4). */
  email?: string;
  plan: MvpTier;
  /** super_admin-only throwaway-account tag (5d-3b D6). */
  uatTest?: boolean;
  /** ADR-124 D5 — the CUSTOMER's country; it decides the zone, which the caller passes below. */
  jurisdiction: string;
},
  /** The zone the customer's country derives (ADR-124 D5). Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  accountId: string;
  customerNumber: string;
  businessName: string;
  email: string;
  website: string;
  uatTest: boolean;
}> {
  return call(zone, '/admin/customers', { method: 'POST', body: JSON.stringify(body) });
}

/** ADR-090 — edit the customer's business name only. Plan/tier changes go
 *  through the audited adminTierOverride (the single "Change plan / tier"
 *  action); this never carries a `plan` field.
 *
 *  ⚠ THAT IS A FACT ABOUT THIS CALLER, NOT ABOUT THE ROUTE. `PUT /admin/customers/
 *  {accountId}` accepts and writes `plan`, so the operator-org refusal had to be put on
 *  the HANDLER, not inferred from this function's body. */
export async function updateCustomerBusinessName(
  accountId: string,
  businessName: string,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ accountId: string; businessName: string }> {
  return call(zone, `/admin/customers/${encodeURIComponent(accountId)}`, {
    method: 'PUT',
    body: JSON.stringify({ businessName }),
  });
}

/**
 * Remove a customer's plan. There is no reassignment counterpart — staff may clear a
 * plan, never set one (Jeff, 12 August 2026). The server's body schema no longer
 * accepts a `tier`, so a caller cannot reintroduce assignment by passing one.
 *
 * ⚠ The ROUTE is still `…/tier-override`: the key is wired into six mirrored Cedar
 * route-action maps and renaming it is a known 409-on-deploy trap. The path is
 * historical; this function's name is the accurate one.
 */
export async function removeCustomerPlan(
  accountId: string,
  reviewNotes: string,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ accountId: string; plan: string; previousPlan: string; idempotent?: boolean }> {
  return call(zone, `/admin/customers/${encodeURIComponent(accountId)}/tier-override`, {
    method: 'POST',
    body: JSON.stringify({ reviewNotes }),
  });
}

/** PL36 — apply a goodwill CREDIT to a customer (ADR-086). The amount is ex-tax
 *  (GST is added on top server-side by applyCredit; the platform never shows a
 *  fabricated GST figure). The route enforces the acting operator's monthly
 *  consultant budget with a hard block (409 `consultant_budget_exceeded`). */
export interface AppliedCredit {
  entryId: string;
  sub: number;
  tax: number;
  total: number;
  reason: string;
  createdAt: string;
}
export async function applyCustomerCredit(
  accountId: string,
  amountSub: number,
  reason: string,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ ok: boolean; entry: AppliedCredit }> {
  return call(zone, `/admin/customers/${encodeURIComponent(accountId)}/credits`, {
    method: 'POST',
    body: JSON.stringify({ amountSub, reason }),
  });
}

export async function listCustomerAdminAudit(
  accountId: string,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<CustomerAdminAuditEntry[]> {
  const resp = await call<{ audit: CustomerAdminAuditEntry[] }>(zone,
    `/admin/customers/${encodeURIComponent(accountId)}/admin-audit`,
  );
  return resp.audit;
}

/** Result of associating/adding a user to a customer account (5d-3b D5). */
export interface AddCustomerUserResult {
  associated?: boolean;
  created?: boolean;
  cognitoSub: string;
  email?: string;
  uatTest?: boolean;
  emailSent?: boolean;
  emailError?: string;
  /** Present only for UAT-account adds — the known sign-in password. */
  uatTestPassword?: string;
}

/**
 * Associate an existing subscriber (`{ cognitoSub }`) or add a new owner
 * (`{ email, name? }`) to a customer account (5d-3b D5).
 */
export async function addCustomerUser(
  accountId: string,
  body: { cognitoSub?: string; email?: string; name?: string },

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<AddCustomerUserResult> {
  return call(zone, `/admin/customers/${encodeURIComponent(accountId)}/users`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// PL38 (ADR-090) — console Billing tab: read the customer's invoice ledger and
// reverse an issued invoice via an offsetting credit note (ADR-083 immutability).

export interface CustomerInvoice {
  invoiceId: string;
  description: string;
  total: number;
  subTotal: number;
  taxTotal: number;
  creditApplied: number;
  amountDue: number;
  status: 'paid' | 'pending' | 'failed' | 'void' | 'reversed';
  createdAt: string;
  domainUrl?: string;
  reversalCreditId?: string;
  reversedAt?: string;
}

export interface CustomerCredit {
  entryId: string;
  sub: number;
  tax: number;
  total: number;
  source: 'referral' | 'staff' | 'consultant' | 'adjustment';
  reason: string;
  appliedToInvoiceId?: string;
  createdAt: string;
}

export interface CustomerBilling {
  billingRoot: string;
  creditBalance: number;
  invoices: CustomerInvoice[];
  credits: CustomerCredit[];
  /** ADR-079 Amendment 1 — present when the account has been deleted and these are its kept
   *  invoices (7 years, read-only). */
  accountDeleted?: { deletedAt: string; businessName: string };
}

/** ADR-079 Amendment 1 — a deleted account whose invoices are still kept. */
export interface DeletedAccountBilling {
  accountId: string;
  businessName: string;
  deletedAt: string;
  invoiceCount: number;
  creditCount: number;
  /** The zone it was read from — every per-record call passes it back. */
  zone: string;
}

/** Every zone's deleted accounts with kept invoices (fan-out, like the customer list). */
export async function listDeletedAccountBilling(): Promise<{
  items: DeletedAccountBilling[];
  failedZones: string[];
}> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await adminApiInZone<{ accounts: Omit<DeletedAccountBilling, 'zone'>[] }>(
      zone,
      '/admin/billing/deleted-accounts',
    );
    return (r.accounts ?? []).map((a) => ({ ...a, zone: zone.code }));
  });
  throwIfAllZonesFailed(results);
  return {
    items: results.flatMap((r) => r.items).sort((a, b) => b.deletedAt.localeCompare(a.deletedAt)),
    failedZones: failedZoneLabels(results),
  };
}

/**
 * PUT /admin/subscriptions/{accountId} — a subscription state change (ADR-013), in the account's
 * OWN zone.
 *
 * ⭐ D9 (5 October 2026) — this call used to be made inline in `SubscriptionStateModal` with the
 * home-zone `adminApi`, so cancelling a US account's subscription from the ops console asked
 * Australia and got `account_not_found`. Every other customer action already took the record's
 * zone; this one now does too, through the same `call` as its siblings.
 */
/**
 * The answer to a subscription change. `warnings` is present when the change LANDED but a step
 * after it did not finish (ADR-013 Amendment 1, 6 October 2026) — e.g. the account is cancelled but
 * its domains could not be archived. Repeating the same change re-runs that step (idempotent).
 */
export interface SubscriptionChangeResult {
  accountId: string;
  subscriptionState: SubscriptionState;
  subscriptionStateChangedAt: string;
  cascadedDomains: number;
  idempotent?: boolean;
  warnings?: Array<{ code: 'domain_cascade_failed' | 'domain_cascade_partial'; domainsNotUpdated?: number }>;
  /** Present with `warnings` — the operator's reference for the request. */
  requestId?: string;
}

export async function updateSubscriptionState(
  accountId: string,
  body: { state: 'active' | 'past_due' | 'suspended' | 'cancelled'; reason?: string },
  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<SubscriptionChangeResult> {
  return call(zone, `/admin/subscriptions/${encodeURIComponent(accountId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function getCustomerBilling(accountId: string,
  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<CustomerBilling> {
  return call<CustomerBilling>(zone, `/admin/customers/${encodeURIComponent(accountId)}/billing`);
}

export async function reverseInvoice(
  accountId: string,
  invoiceId: string,
  reason: string,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ ok: boolean; invoiceId: string; status: string; credit: { total: number } }> {
  return call(zone,     `/admin/customers/${encodeURIComponent(accountId)}/invoices/${encodeURIComponent(invoiceId)}/reverse`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ── ADR-079 §2 — staff account hard delete ──────────────────────────────────
//
// Both calls hit the SAME route. `previewAccountDeletion` sends `{ preview: true }` and never
// deletes; `deleteAccount` sends the typed `confirm`. The server requires a matching `confirm`
// for the destructive path, so a dropped `preview` flag yields a 400 rather than a deletion —
// see DeleteAccountBody's note on the polarity.

export interface AccountDeletionPreview {
  accountId: string;
  businessName: string;
  email: string;
  /** True when an active subscription (or an unreadable billing table) blocks the delete. */
  blocked: boolean;
  blockedReason?: 'subscription_active' | 'billing_unavailable';
  /** The domain named in the "cancel this first" message, when we could resolve one. */
  hostname?: string;
  domainCount: number;
  users: Array<{ sub: string; email: string; loginWillBeDeleted: boolean }>;
  /** B45 — domains whose snippet is still serving, or whose liveness could not be read.
   *  `unknown` means the edge Worker could not be reached: gated identically to `live`
   *  (fail toward the warning) but named distinctly so the modal can say so honestly.
   *  Older responses omit the field, so callers must treat it as optional. */
  liveSnippetDomains?: Array<{
    domainToken: string;
    hostname: string;
    liveness: 'live' | 'unknown';
  }>;
}

export async function previewAccountDeletion(accountId: string,
  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<AccountDeletionPreview> {
  return call<AccountDeletionPreview>(zone,     `/admin/customers/${encodeURIComponent(accountId)}/delete`,
    { method: 'POST', body: JSON.stringify({ preview: true }) },
  );
}

export interface AccountDeletionResult {
  accountId: string;
  deleted: boolean;
  domainsDeleted: number;
  usersDeleted: number;
  loginsDeleted: number;
  loginsRetained: number;
  emailSent: boolean;
}

export async function deleteAccount(
  accountId: string,
  confirm: string,
  /** B45 — set only when the staffer has ticked the live-snippet acknowledgement. Omitted
   *  (rather than sent as `false`) when there is nothing to acknowledge, so the common
   *  no-live-snippet delete puts no new field on the wire. */
  acknowledgeLiveSnippets?: boolean,

  /** The record's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<AccountDeletionResult> {
  return call<AccountDeletionResult>(zone,     `/admin/customers/${encodeURIComponent(accountId)}/delete`,
    {
      method: 'POST',
      body: JSON.stringify({
        confirm,
        ...(acknowledgeLiveSnippets ? { acknowledgeLiveSnippets: true } : {}),
      }),
    },
  );
}

// ⛔ `getImpersonationUrl` was REMOVED on 13 August 2026 along with the route it called,
// `POST /admin/customers/{accountId}/impersonate`. It minted an ADR-023 §9 token so a staffer
// could open a customer's OBW as that customer; ADR-076's BOW/SOW/POW dashboard cards replaced
// it. Do not re-add a client for that route — it no longer exists on any service.
